// La línea de estado de un contratado, como el «escribiendo…/en línea» de Telegram (S10). La usan la lista
// del equipo y la cabecera del chat: una sola frase para «¿qué está pasando con este?».
import { t } from '../../../i18n';
import type { ContratadoDTO } from '../compartido/vocabulario';

export type TonoEstado = 'acento' | 'normal' | 'aviso' | 'error';

// Sin desenfoque de fondo (backdrop-filter): en Chrome/Windows parpadea sobre el lienzo animado del mundo
// (28-09, Pau: «parpadea todo el rato»). Superficie casi opaca en su lugar.
// Rebrand 03-11: el panel flotante de Apple (gris de ventana, filo de 0,5 px y sombra de lo que flota), casi opaco y SIN
// desenfoque, por lo de arriba.
export const SUPERFICIE = 'bg-oficina-elevada/[0.97] shadow-o-flota';

/**
 * Cómo se llama a un agente delante de Pau: por lo que HACE, nunca por un nombre (Pau, 28-09: «si los uso y los
 * tiro, con nombres me lío»). El título lo escribe la recepción al contratar y luego el propio agente.
 */
export function quien(c: Pick<ContratadoDTO, 'tema'>): string {
  return c.tema?.trim() || t('oficinaV2.sinTitulo');
}

export function lineaDeEstado(c: ContratadoDTO): { texto: string; tono: TonoEstado } {
  // Si espera algo de Pau, lo que espera en sus palabras («Necesito tu ok para cambiar la última frase»).
  if (c.visual === 'te_necesita') return { texto: c.teNecesita === 'bloqueo' && c.bloqueo ? c.bloqueo : t(`oficinaV2.teNecesita.${c.teNecesita ?? 'plan'}`), tono: 'acento' };
  if (c.visual === 'error') return { texto: t('oficinaV2.estado.error'), tono: 'error' };
  if (c.visual === 'bloqueado') return { texto: c.titular || t('oficinaV2.estado.bloqueado'), tono: 'aviso' };
  if (c.visual === 'llegando') return { texto: t('oficinaV2.estado.llegando'), tono: 'normal' };
  // 30-09 — integrar no es irse: si la oficina integra su trabajo y luego sigue, no se dice «Terminando».
  if (c.visual === 'cerrando') return { texto: t(c.modoCierre === 'integrar' ? 'oficinaV2.estado.integrando' : 'oficinaV2.estado.cerrando'), tono: 'normal' };
  if (c.visual === 'trabajando') return { texto: c.titular || t('oficinaV2.estado.trabajando'), tono: 'normal' };
  return { texto: c.titular || t('oficinaV2.estado.esperando'), tono: 'normal' };
}

/** Lo último que pasó con él (su último mensaje, su último parte o cuándo llegó), como la hora de Telegram. */
export function ultimoMovimiento(c: Pick<ContratadoDTO, 'ultimoMensaje' | 'ultimoInformeEn' | 'creadoEn'>): string {
  return [c.ultimoMensaje?.en, c.ultimoInformeEn, c.creadoEn].filter((x): x is string => !!x).sort().at(-1)!;
}

/** «hace 12 min», «hace 3 h»: desde cuándo espera (la urgencia de «te necesita» la ordena el tiempo). */
export function hace(iso: string, ahora = Date.now()): string {
  const min = Math.max(0, Math.round((ahora - Date.parse(iso)) / 60_000));
  if (!Number.isFinite(min)) return '';
  if (min < 1) return t('oficinaV2.tiempo.ahora');
  if (min < 60) return t('oficinaV2.tiempo.min', { n: min });
  const h = Math.round(min / 60);
  if (h < 24) return t('oficinaV2.tiempo.h', { n: h });
  return t('oficinaV2.tiempo.d', { n: Math.round(h / 24) });
}
